import React, { useRef, useState, useEffect, ReactNode, MutableRefObject } from "react";

import { Icon, classnames, Loader } from "@culturehq/components";
import { Pagination } from "chq";
import { t } from "app/utils";
import styles from "./carousel.module.scss";

const getSlideLayout = (
  index: number,
  containerRef: MutableRefObject<HTMLDivElement | null>,
  sliderRef: MutableRefObject<HTMLDivElement | null>,
  data: any[],
  elementWidth: number
) => {
  let percent = index * (100 / (data.length + 1));
  const left = index !== 0;
  let right = index !== data.length;

  const container = containerRef.current;
  const slider = sliderRef.current;
  const sliderWidth = elementWidth * (data.length + 1);

  if (
    !(container instanceof HTMLDivElement)
    || !(slider instanceof HTMLDivElement)
  ) {
    return { percent, left, right };
  }

  const pixels = (percent * sliderWidth) / 100;
  const correction = pixels - (slider.clientWidth - container.clientWidth);

  if (sliderWidth <= container.clientWidth) {
    right = false;
  }

  if (correction > 0) {
    percent -= (correction / sliderWidth) * 100;
    right = false;
  }

  if (data.length === 1) {
    right = false;
  }

  return { percent, left, right };
};

interface CarouselProps {
  appending?: boolean;
  carouselFooter?: ReactNode;
  children: ReactNode;
  className?: string;
  data: any[];
  elementWidth?: number;
  onAppendData?: () => void;
  pagination?: Pagination;
}

const Carousel: React.FC<CarouselProps> = ({
  appending,
  carouselFooter,
  children,
  className,
  data,
  elementWidth = 220,
  onAppendData,
  pagination
}) => {
  const [index, setIndex] = useState(0);
  const onNext = () => setIndex(value => value + 1);
  const onPrev = () => setIndex(value => value - 1);

  const containerRef = useRef(null);
  const sliderRef = useRef(null);
  const slideLayout = getSlideLayout(
    index,
    containerRef,
    sliderRef,
    data,
    elementWidth
  );

  useEffect(() => {
    if (
      !slideLayout.right
      && data.length !== 0
      && pagination?.currentPage !== pagination?.totalPages
      && onAppendData
    ) {
      onAppendData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideLayout.right, pagination, data]);

  return (
    <div className={classnames(className, "Carousel")} style={{ position: "relative" }}>
      <button
        aria-label={t("Previous")}
        type="button"
        className={classnames("Carousel-Btn", "Carousel-Btn__Left", styles.direction, styles.left)}
        onClick={onPrev}
        disabled={!slideLayout.left}
      >
        <Icon icon="chevron-left" />
      </button>
      <button
        aria-label={t("Next")}
        type="button"
        className={classnames("Carousel-Btn", "Carousel-Btn__Right", styles.direction, styles.right)}
        onClick={onNext}
        disabled={!slideLayout.right}
      >
        <Icon icon="chevron-right" />
      </button>
      <div
        ref={containerRef}
        className={classnames(
          "no-scrollbar",
          "Carousel-Container",
          data.length === 0 ? styles.emptySliderContainer : styles.container
        )}
      >
        <div
          ref={sliderRef}
          className={classnames(styles.slider, "Trending-Slider")}
          style={{ transform: `translateX(-${slideLayout.percent}%)` }}
        >
          {children}
          {appending && <Loader loading />}
        </div>
      </div>
      {carouselFooter}
    </div>
  );
};

export default Carousel;
